import React, { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Progress } from "../ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Loader2, Sparkles, FileText, Cloud, Link as LinkIcon, Edit } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function InputTab({
  contentTitle,
  setContentTitle,
  sourceContent,
  setSourceContent,
  targetAudience,
  setTargetAudience,
  contentTone,
  setContentTone,
  isGenerating,
  generationProgress,
  generateContent,
  useMockData,
  settings,
  connectGoogleDrive,
  loadProjects,
  loadDriveFiles,
  driveConnected
}) {
  const [inputMode, setInputMode] = useState('paste'); // 'paste' or 'url'
  const [urlInput, setUrlInput] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [fetchedMetadata, setFetchedMetadata] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const wordCount = sourceContent.trim().split(/\s+/).filter(w => w).length;

  const handleFetchUrl = async () => {
    if (!urlInput.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(urlInput);
    } catch (e) {
      toast.error('Please enter a valid URL');
      return;
    }

    setIsFetching(true);

    try {
      const response = await axios.post(`${API}/generate/fetch-url`, {
        url: urlInput
      });

      console.log('Fetch response:', response.data);

      if (response.data && response.data.success) {
        // Auto-populate fields
        setContentTitle(response.data.metadata.title);
        setSourceContent(response.data.content);
        setFetchedMetadata(response.data.metadata);
        setIsEditing(true); // Allow editing immediately after fetch
        toast.success('Content fetched successfully! You can now edit and generate.');
      } else {
        toast.error('Failed to extract content from URL');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch URL content');
    } finally {
      setIsFetching(false);
    }
  };

  const formats = [
    { icon: "💼", title: "LinkedIn", description: "7-slide carousel" },
    { icon: "🐦", title: "Twitter", description: "7-tweet thread" },
    { icon: "📘", title: "Facebook", description: "5 engaging posts" },
    { icon: "🎥", title: "Video Scripts", description: "5 short-form scripts" },
    { icon: "📧", title: "Email Sequence", description: "5 nurture emails" },
    { icon: "🎙️", title: "Podcast", description: "7 episode sections" },
    { icon: "📊", title: "Infographic", description: "8-section visual" }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="text-center space-y-4">
        <h2 className="text-4xl font-bold text-white">
          Paste Your Blog Post
        </h2>
        <p className="text-xl text-white/60">
          Transform it into 7 high-performing content formats with AI
        </p>
      </div>

      {/* Load Buttons */}
      <div className="flex gap-4 justify-center">
        <Button
          variant="outline"
          onClick={driveConnected ? loadDriveFiles : connectGoogleDrive}
          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
        >
          <Cloud className="w-4 h-4 mr-2" />
          {driveConnected ? "Load from Google Drive" : "Connect Google Drive"}
        </Button>
        <Button
          variant="outline"
          onClick={loadProjects}
        >
          <FileText className="w-4 h-4 mr-2" />
          Load Saved Project
        </Button>
      </div>

      {/* Input Form */}
      <Card className="card">
        <CardContent className="pt-6 space-y-6">
          {/* Toggle between Paste and URL */}
          <div className="flex gap-2 justify-center mb-4">
            <Button
              size="sm"
              variant={inputMode === 'paste' ? 'default' : 'outline'}
              onClick={() => setInputMode('paste')}
              className={inputMode === 'paste' ? 'bg-[#00d4aa] text-black hover:bg-[#00e6bb]' : ''}
            >
              <FileText className="w-4 h-4 mr-1" />
              Paste Text
            </Button>
            <Button
              size="sm"
              variant={inputMode === 'url' ? 'default' : 'outline'}
              onClick={() => setInputMode('url')}
              className={inputMode === 'url' ? 'bg-[#00d4aa] text-black hover:bg-[#00e6bb]' : ''}
            >
              <LinkIcon className="w-4 h-4 mr-1" />
              Import from URL
            </Button>
          </div>

          {/* URL Import Section */}
          {inputMode === 'url' && (
            <div className="space-y-4 p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  Blog Post URL
                </label>
                <div className="flex gap-2">
                  <Input
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://example.com/blog/article"
                    className="bg-white/5 border-white/10 text-white flex-1"
                  />
                  <Button
                    onClick={handleFetchUrl}
                    disabled={isFetching || !urlInput.trim()}
                    className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
                  >
                    {isFetching ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Fetching...
                      </>
                    ) : (
                      'Fetch'
                    )}
                  </Button>
                </div>
                <p className="text-xs text-white/40 mt-1">
                  Paste any public blog URL to automatically extract content
                </p>
              </div>

              {/* Show metadata after fetch */}
              {fetchedMetadata && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 text-sm font-medium mb-2">✓ Content Extracted Successfully</p>
                  <div className="text-xs text-white/60 space-y-1">
                    <p><strong>Title:</strong> {fetchedMetadata.title}</p>
                    <p><strong>Words:</strong> {fetchedMetadata.word_count}</p>
                    {fetchedMetadata.published_date && (
                      <p><strong>Published:</strong> {fetchedMetadata.published_date}</p>
                    )}
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className="text-[#00d4aa] hover:text-[#00e6bb] flex items-center gap-1 mt-2"
                    >
                      <Edit className="w-3 h-3" />
                      {isEditing ? 'Lock Content' : 'Edit Content'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Content Title
            </label>
            <Input
              value={contentTitle}
              onChange={(e) => setContentTitle(e.target.value)}
              placeholder="e.g., 10 Ways to Improve Your SEO in 2025"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
          </div>

          {/* Dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Target Audience
              </label>
              <Select value={targetAudience} onValueChange={setTargetAudience}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trades">Trades & Construction</SelectItem>
                  <SelectItem value="home-services">Home Services</SelectItem>
                  <SelectItem value="professional">Professional Services</SelectItem>
                  <SelectItem value="ecommerce">E-commerce</SelectItem>
                  <SelectItem value="saas">SaaS / Tech</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Content Tone
              </label>
              <Select value={contentTone} onValueChange={setContentTone}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">Direct & Practical</SelectItem>
                  <SelectItem value="casual">Casual & Friendly</SelectItem>
                  <SelectItem value="professional">Professional & Formal</SelectItem>
                  <SelectItem value="educational">Educational & Detailed</SelectItem>
                  <SelectItem value="persuasive">Persuasive & Sales-focused</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Content Textarea */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Blog Post Content
            </label>
            <Textarea
              value={sourceContent}
              onChange={(e) => setSourceContent(e.target.value)}
              placeholder={inputMode === 'url' ? 'Fetched content will appear here...' : "Paste your blog post content here...\n\nThe more detailed your content, the better the AI-generated outputs will be."}
              rows={12}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 resize-none"
            />
            <div className="word-count mt-2 text-right">
              {wordCount} words
            </div>
          </div>

          {/* Generate Button */}
          <div className="space-y-4">
            <Button
              onClick={generateContent}
              disabled={isGenerating || !contentTitle || !sourceContent}
              className="w-full h-12 bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold text-lg"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating... {generationProgress}%
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate All Content
                </>
              )}
            </Button>

            {/* Progress Bar */}
            {isGenerating && (
              <div className="space-y-2">
                <Progress value={generationProgress} className="h-2" />
                <p className="text-sm text-center text-white/60">
                  Claude AI is creating your content formats...
                </p>
              </div>
            )}

            {/* Mock Data Button */}
            <div className="text-center">
              <Button
                variant="ghost"
                onClick={useMockData}
                className="text-white/40 hover:text-white/80 text-sm"
              >
                Or try with sample content
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Format Preview Grid */}
      <div>
        <h3 className="text-2xl font-bold text-white text-center mb-6">
          What You'll Get
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {formats.map((format, index) => (
            <Card key={index} className="card text-center">
              <CardContent className="pt-6">
                <div className="text-4xl mb-3">{format.icon}</div>
                <h4 className="font-semibold text-white mb-1">{format.title}</h4>
                <p className="text-sm text-white/60">{format.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export default InputTab;